import { useEffect, useState } from "react";
import { getShareCount, type ShareEntityType } from "@/services/shareService";
import { isPaidPlan } from "@/utils/plans";
import LockedFeature from "@/components/base/LockedFeature";

interface ShareCounterProps {
  entityType: ShareEntityType;
  slug: string;
  /** The listing's plan slug — the counter is a paid-only feature. */
  plan?: string;
  /**
   * Tourist spots aren't plan-based, so their counter stays visible
   * regardless of plan. Set this to true to bypass the paywall.
   */
  alwaysVisible?: boolean;
}

/**
 * Public "Shared N times" counter shown next to the share buttons.
 * Paid-only: Free-plan listings see a locked upgrade prompt instead,
 * unless `alwaysVisible` is set (used on tourist-spot pages).
 */
export default function ShareCounter({
  entityType,
  slug,
  plan,
  alwaysVisible = false,
}: ShareCounterProps) {
  const paid = alwaysVisible || isPaidPlan(plan);
  const [count, setCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!paid || !slug) return undefined;
    let active = true;
    getShareCount(entityType, slug)
      .then((value) => {
        if (active) setCount(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [paid, entityType, slug]);

  if (!paid) {
    return <LockedFeature label="Share counter" />;
  }

  if (failed) {
    return null;
  }

  const label =
    count === null
      ? "Counting shares"
      : `${count.toLocaleString("en-IN")} ${count === 1 ? "share" : "shares"}`;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900"
      title="Total times this page has been shared"
    >
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className={`text-[13px] ${count === null ? "ri-loader-4-line animate-spin" : "ri-share-forward-line"}`} />
      </span>
      {label}
    </span>
  );
}